import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropZone", "tagItem", "deleteButton"]

  connect() {
    this.setupDragAndDrop()
    this.setupDeleteButtons()
  }

  setupDragAndDrop() {
    // Setup draggable sidebar tags (only artwork tags)
    const draggableTags = document.querySelectorAll('.sidebar-tag-container[draggable="true"]')
    draggableTags.forEach(tag => {
      tag.addEventListener('dragstart', this.handleDragStart.bind(this))
      tag.addEventListener('dragend', this.handleDragEnd.bind(this))
    })

    // Setup drop zones
    this.dropZoneTargets.forEach(zone => {
      zone.addEventListener('dragover', this.handleDragOver.bind(this))
      zone.addEventListener('dragleave', this.handleDragLeave.bind(this))
      zone.addEventListener('drop', this.handleDrop.bind(this))
    })
  }

  setupDeleteButtons() {
    this.deleteButtonTargets.forEach(button => {
      // Only handle delete buttons that have artwork-id (not sidebar tag deletion)
      if (button.dataset.artworkId) {
        button.addEventListener('click', this.handleDeleteClick.bind(this))
      }
    })
  }

  handleDragStart(event) {
    const tag = event.currentTarget
    tag.classList.add('dragging')
    
    event.dataTransfer.setData('text/plain', JSON.stringify({
      tagId: tag.dataset.tagId,
      tagName: tag.dataset.tagName,
      tagType: tag.dataset.tagType
    }))
    
    event.dataTransfer.effectAllowed = 'copy'
  }

  handleDragEnd(event) {
    const tag = event.currentTarget
    tag.classList.remove('dragging')
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    event.currentTarget.classList.add('drag-over')
  }

  handleDragLeave(event) {
    event.currentTarget.classList.remove('drag-over')
  }

  handleDrop(event) {
    event.preventDefault()
    const dropZone = event.currentTarget
    dropZone.classList.remove('drag-over')
    
    const data = JSON.parse(event.dataTransfer.getData('text/plain'))
    const artworkId = dropZone.dataset.artworkId
    
    this.addTagToArtwork(artworkId, data.tagId, data.tagName)
  }

  addTagToArtwork(artworkId, tagId, tagName) {
    const url = `/dashboard/artworks/${artworkId}/add_tag`
    
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ tag_id: tagId })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        this.addTagToDisplay(artworkId, data.tag)
      } else {
        // Silently fail for duplicate associations, only show other errors
        if (data.error !== 'Tag is already associated with this artwork.') {
          this.showError(data.error || 'Failed to add tag')
        }
      }
    })
    .catch(error => {
      console.error('Error:', error)
      this.showError('An unexpected error occurred')
    })
  }

  addTagToDisplay(artworkId, tag) {
    const dropZone = this.dropZoneTargets.find(zone => zone.dataset.artworkId === artworkId)
    const tagsContainer = dropZone.querySelector('.artwork-tags-container')
    
    const tagHtml = `
      <div class="artwork-tag-item" 
           data-tag-id="${tag.id}"
           data-artwork-tags-target="tagItem">
        <div class="artwork-tag-container">
          <span class="artwork-tag-name">${tag.name}</span>
          <button class="artwork-tag-delete" 
                  data-artwork-id="${artworkId}" 
                  data-tag-id="${tag.id}" 
                  data-tag-name="${tag.name}"
                  data-artwork-tags-target="deleteButton">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    `
    
    tagsContainer.insertAdjacentHTML('beforeend', tagHtml)
    
    // Setup delete button for the new tag
    const newDeleteButton = tagsContainer.querySelector(`[data-tag-id="${tag.id}"] .artwork-tag-delete`)
    newDeleteButton.addEventListener('click', this.handleDeleteClick.bind(this))
  }

  handleDeleteClick(event) {
    event.preventDefault()
    event.stopPropagation()
    const button = event.currentTarget
    const artworkId = button.dataset.artworkId
    const tagId = button.dataset.tagId
    const tagName = button.dataset.tagName
    
    if (confirm(`Are you sure you want to remove the tag "${tagName}" from this artwork?`)) {
      this.removeTagFromArtwork(artworkId, tagId, button)
    } else {
      // User clicked Cancel, do nothing
      return false
    }
  }

  removeTagFromArtwork(artworkId, tagId, button) {
    const url = `/dashboard/artworks/${artworkId}/remove_tag`
    
    fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ tag_id: tagId })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        const tagItem = button.closest('.artwork-tag-item')
        tagItem.remove()
      } else {
        this.showError(data.error || 'Failed to remove tag')
      }
    })
    .catch(error => {
      console.error('Error:', error)
      this.showError('An unexpected error occurred')
    })
  }

  showError(message) {
    // Create a temporary error message
    const errorDiv = document.createElement('div')
    errorDiv.className = 'alert alert-danger alert-dismissible fade show'
    errorDiv.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `
    
    const container = document.querySelector('.subnav-menu')
    container.insertAdjacentElement('afterend', errorDiv)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.remove()
      }
    }, 5000)
  }
} 